import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { JobStatusBadge, StatusFilterChip } from "@/components/StatusPill";
import { ClipboardList, Archive as ArchiveIcon, Loader2, Plus, Filter, RotateCcw, MessageSquare, Mail } from "lucide-react";
import DataTable from "@/components/DataTable";
import { Pill } from "@/components/StatusPill";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import JobForm from "@/components/JobForm";
import { DEPT_COLOUR } from "@/lib/departments";
import { formatDateTime } from "@/lib/datetime";
import { SourceBadge } from "@/components/EnquirySource";
import { NewEnquiriesBanner } from "@/components/NewEnquiriesBanner";

export const columns = [
  {
    key: "current_department",
    label: "Department",
    render: (v) => (v ? <Pill colour={DEPT_COLOUR[v]}>{v}</Pill> : <span className="text-slate-300">—</span>),
  },
  { key: "created_at", label: "Created At", render: (v) => <span className="whitespace-nowrap text-slate-600">{formatDateTime(v)}</span> },
  { key: "legacy_id", label: "ID", mono: true, mandatory: true, defaultWidth: 130 },
  { key: "title", label: "Title", strong: true, truncate: true, render: (v, r) => (
    <span className="inline-flex items-center gap-1.5 min-w-0">
      <span className="truncate">{v}</span>
      {r.sms_unread > 0 && (
        <Link to={`/tasks/${r.legacy_id}?sms=1`} onClick={(e) => e.stopPropagation()} title={`${r.sms_unread} unread SMS repl${r.sms_unread === 1 ? "y" : "ies"}`}
          className="shrink-0 inline-flex items-center h-5 px-1.5 rounded-full bg-rose-600 text-white text-[11px] font-semibold" data-testid={`task-sms-unread-${r.legacy_id}`}>
          {r.sms_unread} SMS
        </Link>
      )}
    </span>
  ) },
  { key: "company", label: "Company", truncate: true },
  { key: "public_contact", label: "Customer", truncate: true },
  { key: "customer_email", label: "Email", render: (v, r) => (
    !v ? <span className="text-slate-300">—</span>
      : v.startsWith("•") ? <span>{v}</span>
      : <a href={`mailto:${v}?subject=${encodeURIComponent(`${r.title || "Your enquiry"} (Ref ${r.legacy_id})`)}`} onClick={(e) => e.stopPropagation()}
          title="Email this customer" className="inline-flex items-center gap-1 max-w-[260px] truncate text-indigo-600 hover:text-indigo-800 hover:underline" data-testid={`task-email-${r.legacy_id}`}>
          <Mail className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{v}</span>
        </a>
  ) },
  { key: "customer_phone", label: "Tel", render: (v, r) => (
    !v ? <span className="text-slate-300">—</span>
      : v.startsWith("•") ? <span className="whitespace-nowrap">{v}</span>
      : <Link to={`/tasks/${r.legacy_id}?sms=1`} onClick={(e) => e.stopPropagation()} title="Text this customer"
          className="inline-flex items-center gap-1 whitespace-nowrap text-indigo-600 hover:text-indigo-800 hover:underline" data-testid={`task-phone-sms-${r.legacy_id}`}>
          <MessageSquare className="w-3.5 h-3.5" />{v}
        </Link>
  ) },
  { key: "enquiry_source", label: "Source", render: (v, r) => <SourceBadge name={v} testid={`task-source-${r.legacy_id}`} /> },
  { key: "sales_contact", label: "Sales Contact", truncate: true },
  { key: "job_summary_status", label: "Job Summary", render: (v, r) => (
    v === "REBOOK"
      ? <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 text-amber-700 px-2 py-0.5 text-xs font-semibold whitespace-nowrap"
          title={r.rebook?.reason ? `Rebook — ${r.rebook.reason}` : "Rebook"} data-testid={`task-rebook-chip-${r.legacy_id}`}>
          <RotateCcw className="w-3 h-3" /> REBOOK
          {r.rebook?.reason ? <span className="font-normal text-amber-600/90 max-w-[130px] truncate">· {r.rebook.reason}</span> : null}
        </span>
      : <JobStatusBadge value={v} to={`?status=${encodeURIComponent(v)}`} data-testid={`task-status-badge-${r.legacy_id}`} />
  ) },
  {
    key: "req_total",
    label: "Requirements",
    align: "center",
    render: (v, r) => {
      const t = r.req_total || 0;
      if (!t) return <span className="text-slate-300">—</span>;
      const d = r.req_done || 0;
      const cls = d >= t ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : d > 0 ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-slate-50 text-slate-500 border-slate-200";
      return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold tabular-nums ${cls}`} data-testid={`task-req-badge-${r.legacy_id}`}>{d}/{t}</span>;
    },
  },
  { key: "venue_name", label: "Venue", truncate: true },
  { key: "venue_postcode", label: "Postcode", mono: true },
  {
    key: "task_type",
    label: "Job Type",
    render: (v, r) => (v ? <Pill colour={r.task_type_colour}>{v}</Pill> : <span className="text-slate-300">—</span>),
  },
  { key: "work_type", label: "Work Type" },
  { key: "depot", label: "Depot" },
  { key: "work_instructions", label: "Work Instructions", truncate: true },
  {
    key: "status",
    label: "Status",
    render: (v, r) => (v ? <Pill colour={r.status_colour}>{v}</Pill> : <span className="text-slate-300">—</span>),
  },
  { key: "task_step", label: "Job Step", align: "right" },
  { key: "instructed_date", label: "Instructed Date" },
  {
    key: "rebook_reason",
    label: "Rebook Reason",
    defaultHidden: true,
    sortable: false,
    truncate: true,
    render: (_, r) => (r.rebook?.reason ? <span className="text-slate-600">{r.rebook.reason}</span> : <span className="text-slate-300">—</span>),
  },
  {
    key: "rebook_engineer",
    label: "Engineer",
    defaultHidden: true,
    sortable: false,
    render: (_, r) => (r.rebook?.by_name ? <span className="text-slate-600">{r.rebook.by_name}</span> : <span className="text-slate-300">—</span>),
  },
  {
    key: "original_appointment",
    label: "Original Appointment",
    defaultHidden: true,
    sortable: false,
    render: (v) => (v ? <span className="whitespace-nowrap text-slate-600">{v}</span> : <span className="text-slate-300">—</span>),
  },
  { key: "updated_at", label: "Last Updated", defaultHidden: true, render: (v) => <span className="whitespace-nowrap text-slate-600">{formatDateTime(v)}</span> },
];

const REBOOK_VIEW_KEYS = [
  "legacy_id", "public_contact", "rebook_reason", "rebook_engineer",
  "original_appointment", "venue_name", "venue_postcode", "current_department",
  "updated_at", "job_summary_status",
];
const REBOOK_SYSTEM_VIEW = { id: "sys-rebook", name: "Rebook Jobs", system: true, scope: "rebook" };


export default function TasksPage() {
  const [meta, setMeta] = useState(null);
  const [dept, setDept] = useState(null);
  const { can } = useAuth();
  const [showAdd, setShowAdd] = useState(false);
  const [rebookMode, setRebookMode] = useState(false);
  const [rebookCount, setRebookCount] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("new") && can("orders.manage")) {
      setShowAdd(true);
      const p = new URLSearchParams(searchParams); p.delete("new"); setSearchParams(p, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Single source of truth for the backlog count: the SAME REBOOK filter the
  // built-in "Rebook Jobs" view uses (tenant-scoped + RBAC enforced server-side).
  // Re-fetches on mount and whenever the user enters/leaves the Rebook view so the
  // badge reflects jobs moving in or out of REBOOK.
  useEffect(() => {
    let active = true;
    api.get("/tasks", { params: { job_summary_status: "REBOOK", page: 1, page_size: 1 } })
      .then((r) => active && setRebookCount(r.data?.total || 0))
      .catch(() => active && setRebookCount(0));
    return () => { active = false; };
  }, [rebookMode]);

  const rebookColumns = useMemo(() => REBOOK_VIEW_KEYS.map((k) => {
    const c = columns.find((x) => x.key === k) || { key: k, label: k };
    const copy = { ...c, defaultHidden: false };
    if (k === "updated_at") copy.defaultSort = { key: "updated_at", dir: "desc" };
    return copy;
  }), []);

  const urlWorkType = searchParams.get("work_type") || "";
  const urlOverdue = searchParams.get("overdue") === "1";
  const urlStatus = searchParams.get("status") || "";
  const clearStatus = () => { const p = new URLSearchParams(searchParams); p.delete("status"); setSearchParams(p); };
  const statusParam = urlStatus ? { job_summary_status: urlStatus } : {};
  const hasUrlFilter = !!urlWorkType || urlOverdue;
  const filterLabel = urlOverdue ? "Overdue jobs (open > 30 days)" : urlWorkType ? `Work type: ${urlWorkType}` : "";

  useEffect(() => {
    api.get("/tasks-departments/meta")
      .then((r) => {
        setMeta(r.data);
        const vis = r.data.visible || [];
        setDept(vis.find((d) => d !== "Archive" && d !== "Shop") || vis.find((d) => d !== "Archive") || vis[0] || null);
      })
      .catch(() => setMeta({ visible: [] }));
  }, []);

  if (!meta) {
    return (
      <div className="p-10 flex items-center gap-2 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading job queues…
      </div>
    );
  }

  const visible = meta.visible || [];
  const activeVisible = visible.filter((d) => !["Archive", "Shop", "HR", "Maintenance"].includes(d));
  const hasArchive = visible.includes("Archive");

  if (visible.length === 0) {
    return (
      <div className="p-6 lg:p-8" data-testid="page-tasks">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><ClipboardList className="w-5 h-5" /></span>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Jobs</h1>
            <p className="text-sm text-slate-500 mt-0.5">Department queues</p>
          </div>
        </div>
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500" data-testid="tasks-no-department-access">
          Your role does not have access to any job department queues.
        </div>
      </div>
    );
  }

  const deptTabs = (
    <>
    <Select value={rebookMode ? "" : (dept || "")} onValueChange={(d) => { setDept(d); setRebookMode(false); }}>
      <SelectTrigger className="h-9 w-auto min-w-[190px] bg-white" data-testid="dept-select">
        <SelectValue placeholder="Department">{dept && !rebookMode ? `Department: ${dept}` : undefined}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {activeVisible.map((d) => <SelectItem key={d} value={d} data-testid={`dept-option-${d}`}>{d}</SelectItem>)}
        {hasArchive && activeVisible.length > 0 && <SelectSeparator />}
        {hasArchive && <SelectItem value="Archive" data-testid="dept-option-Archive"><span className="inline-flex items-center gap-1.5"><ArchiveIcon className="w-3.5 h-3.5" /> Archive</span></SelectItem>}
      </SelectContent>
    </Select>
    <StatusFilterChip value={urlStatus} onClear={clearStatus} />
    </>
  );

  return (
    <div data-testid="page-tasks-wrapper">
      <div className="px-6 lg:px-8 pt-3 lg:pt-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <img src="/brand/illustrations/jobs.webp" alt="" className="hidden sm:block w-16 h-auto shrink-0 select-none transition-[transform,filter] duration-300 ease-out hover:-translate-y-1 hover:scale-[1.04] hover:drop-shadow-[0_8px_10px_rgba(15,23,42,0.18)] motion-reduce:transition-none motion-reduce:hover:transform-none" data-testid="jobs-header-illustration" />
            <div>
              <h1 className="ted-page-title">Jobs</h1>
              <p className="ted-subtle mt-1">Department queues · a job is only visible to its current department</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {rebookCount > 0 && (
              <button
                onClick={() => setRebookMode(true)}
                data-testid="jobs-rebook-backlog-indicator"
                title="View jobs needing rescheduling"
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors px-3 py-1.5 text-sm font-semibold whitespace-nowrap"
              >
                <RotateCcw className="w-4 h-4" />
                <span className="hidden sm:inline" data-testid="jobs-rebook-backlog-count">{rebookCount} {rebookCount === 1 ? "job needs" : "jobs need"} rescheduling</span>
                <span className="sm:hidden">REBOOK {rebookCount}</span>
              </button>
            )}
            {can("orders.manage") && (
              <Button onClick={() => setShowAdd(true)} className="gap-1.5 h-10 px-5 bg-indigo-600 hover:bg-indigo-700 text-white shrink-0" data-testid="jobs-new-btn">
                <Plus className="w-4 h-4" /> New Job
              </Button>
            )}
          </div>
        </div>
      <NewEnquiriesBanner />
      {hasUrlFilter ? (
        <div className="px-6 lg:px-8 pb-2">
          <div className="flex items-center gap-2 flex-wrap" data-testid="tasks-url-filter">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-3 py-1.5 text-sm font-semibold"><Filter className="w-3.5 h-3.5" /> {filterLabel}</span>
            <button onClick={() => setSearchParams({})} className="text-sm text-slate-500 hover:text-indigo-600 hover:underline" data-testid="tasks-clear-filter">Clear filter</button>
          </div>
        </div>
      ) : null}
      </div>

      {rebookMode ? (
        <DataTable
          key="rebook"
          dataset="tasks"
          tableId="tasks_rebook"
          section
          title="Rebook Jobs"
          icon={ClipboardList}
          toolbarStart={deptTabs}
          columns={rebookColumns}
          params={{ job_summary_status: "REBOOK" }}
          searchPlaceholder="Search rebook jobs — title, ID, postcode…"
          rowLink={(r) => `/tasks/${r.legacy_id}`}
          compactToggle
          systemViews={[REBOOK_SYSTEM_VIEW]}
          activeViewId="sys-rebook"
          onSystemView={(v) => v.scope === "rebook" && setRebookMode(true)}
        />
      ) : hasUrlFilter ? (
        <DataTable
          key={`filtered-${filterLabel}`}
          dataset="tasks"
          title={filterLabel}
          icon={ClipboardList}
          columns={columns}
          params={{ ...(urlWorkType ? { work_type: urlWorkType } : {}), ...(urlOverdue ? { overdue: true } : {}), ...statusParam }}
          searchPlaceholder="Search title, ID, postcode, status…"
          rowLink={(r) => `/tasks/${r.legacy_id}`}
          compactToggle
          systemViews={[REBOOK_SYSTEM_VIEW]}
          onSystemView={(v) => v.scope === "rebook" && setRebookMode(true)}
        />
      ) : dept && (
        <DataTable
          key={dept}
          dataset="tasks"
          section
          title={`${dept} queue`}
          hideHeader
          toolbarStart={deptTabs}
          columns={columns}
          params={{ department: dept, ...statusParam }}
          searchPlaceholder="Search title, ID, postcode, status…"
          rowLink={(r) => `/tasks/${r.legacy_id}`}
          compactToggle
          systemViews={[REBOOK_SYSTEM_VIEW]}
          onSystemView={(v) => v.scope === "rebook" && setRebookMode(true)}
        />
      )}
      {showAdd && <JobForm mode="create" onClose={() => setShowAdd(false)} onSaved={() => setShowAdd(false)} />}
    </div>
  );
}
